/**
 * StepChecklistRow — single row in the processing checklist.
 * Card style per spec V15a: white bg, border, rounded corners.
 * React.memo for performance. AC-3.3
 */
import React, { JSX, memo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import type { FileProcessingStep } from 'src/types/aiFileUploadV2';

type StepStatus = FileProcessingStep['status'];

interface StepChecklistRowProps {
  displayName: string;
  status: StepStatus;
}

const StepChecklistRow = memo(
  ({ displayName, status }: StepChecklistRowProps): JSX.Element => {
    const theme = useTheme();

    const isRunning = status === 'running';
    const isFailed = status === 'failed';
    const isPending = status === 'pending' || status === 'skipped';

    const borderColor = isRunning
      ? theme.palette.primary.main
      : isFailed
      ? theme.palette.error.main
      : theme.palette.divider;

    const bgColor = isPending
      ? theme.palette.background.neutral
      : theme.palette.background.paper;

    const textColor = isRunning
      ? theme.palette.primary.main
      : isPending
      ? theme.palette.text.disabled
      : theme.palette.text.primary;

    const textWeight = isRunning ? 600 : 400;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.75,
          borderRadius: '14px',
          border: `1px solid ${borderColor}`,
          backgroundColor: bgColor,
        }}
        data-testid={`step-row-${status}`}
      >
        {/* Icon */}
        <StepIcon status={status} />

        <Typography
          sx={{
            fontSize: 14,
            lineHeight: 1.4,
            color: textColor,
            fontWeight: textWeight,
          }}
        >
          {displayName}
          {isRunning ? '\u2026' : ''}
        </Typography>
      </Box>
    );
  }
);

function StepIcon({ status }: { status: StepStatus }): JSX.Element {
  const theme = useTheme();

  if (status === 'completed') {
    // Filled green circle-check (lucide circle-check style using SVG)
    return (
      <svg
        aria-hidden="true"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke={theme.palette.primary.main}
        strokeWidth="2"
        style={{ flexShrink: 0 }}
      >
        <circle
          cx="12"
          cy="12"
          r="10"
          fill={theme.palette.primary.main}
          stroke="none"
        />
        <polyline
          points="7 12 10 15 17 9"
          stroke={theme.palette.common.white}
          strokeWidth="2"
          fill="none"
        />
      </svg>
    );
  }

  if (status === 'running') {
    // Spinning loader circle in primary colour
    return (
      <svg
        aria-hidden="true"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke={theme.palette.primary.main}
        strokeWidth="2"
        style={{ flexShrink: 0, animation: 'rh-spin 1s linear infinite' }}
      >
        <style>{`@keyframes rh-spin { to { transform: rotate(360deg); } }`}</style>
        <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
        <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
      </svg>
    );
  }

  if (status === 'failed') {
    return (
      <svg
        aria-hidden="true"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke={theme.palette.error.main}
        strokeWidth="2"
        style={{ flexShrink: 0 }}
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="15" y1="9" x2="9" y2="15" />
        <line x1="9" y1="9" x2="15" y2="15" />
      </svg>
    );
  }

  // pending / skipped — empty circle
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke={theme.palette.divider}
      strokeWidth="2"
      style={{ flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="10" />
    </svg>
  );
}

StepChecklistRow.displayName = 'StepChecklistRow';

export default StepChecklistRow;
